(() => {
  'use strict';

  const text = (value) => typeof value === 'string' ? value.trim() : '';
  const isObject = (value) => Boolean(value && typeof value === 'object' && !Array.isArray(value));
  const aliases = {
    face: 'front', face_avant: 'front', devant: 'front', dos: 'back', rear: 'back',
    coeur: 'heart', left_chest: 'heart', leftchest: 'heart', poitrine: 'chest',
    anti_coeur: 'chest', anticoeur: 'chest', antiheart: 'chest', anti_heart: 'chest',
    center_chest: 'chest', centre_chest: 'chest', chest_center: 'chest', chest_centre: 'chest',
    haut_du_dos: 'upper_back', upperback: 'upper_back', hautdos: 'upper_back', manche: 'sleeve',
    manche_gauche: 'left_sleeve', leftsleeve: 'left_sleeve', sleeveleft: 'left_sleeve',
    sleeve_left: 'left_sleeve', left_manche: 'left_sleeve', gauche_manche: 'left_sleeve',
    manche_droite: 'right_sleeve', rightsleeve: 'right_sleeve', sleeveright: 'right_sleeve',
    sleeve_right: 'right_sleeve', right_manche: 'right_sleeve', droite_manche: 'right_sleeve',
    epaule_gauche: 'left_shoulder', leftshoulder: 'left_shoulder', shoulder_left: 'left_shoulder',
    left_epaule: 'left_shoulder', gauche_epaule: 'left_shoulder',
    epaule_droite: 'right_shoulder', rightshoulder: 'right_shoulder', shoulder_right: 'right_shoulder',
    right_epaule: 'right_shoulder', droite_epaule: 'right_shoulder',
    capuche: 'hood', interieur: 'inside', interior: 'inside', nuque: 'nape', col: 'neck', etiquette: 'label',
    couverture_avant: 'front_cover', frontcover: 'front_cover', recto: 'front_cover',
    couverture_arriere: 'back_cover', backcover: 'back_cover', verso: 'back_cover',
    tour_complet: 'wrap', tourcomplet: 'wrap', full_wrap: 'wrap', fullwrap: 'wrap', '360': 'wrap',
    sur_mesure: 'custom', surmesure: 'custom', special_request: 'custom',
    demande_speciale: 'custom', demande_special: 'custom', special: 'custom',
  };
  const knownZones = new Set([
    'heart', 'chest', 'front', 'back', 'upper_back', 'sleeve', 'left_sleeve', 'right_sleeve',
    'left_shoulder', 'right_shoulder', 'hood', 'nape', 'neck', 'label', 'front_cover', 'back_cover',
    'wrap', 'inside', 'custom',
  ]);
  const normalizeZone = (value) => {
    const key = text(value).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/œ/g, 'oe').replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    const unprefixed = key.replace(/^(?:(?:zone|view|placement)_)+/u, '');
    const canonical = Object.prototype.hasOwnProperty.call(aliases, unprefixed) ? aliases[unprefixed] : unprefixed;
    // Prefix aliases apply to named zones only; generated IDs remain distinct.
    return knownZones.has(canonical) ? canonical : key;
  };

  // Some stored cart manifests contain HTML entities inside the JSON string.
  // Decode structured JSON only after ordinary parsing fails; never insert HTML.
  const parseJson = (value) => {
    if (isObject(value) || Array.isArray(value)) return value;
    let source = text(value);
    for (let attempt = 0; attempt < 3 && source; attempt += 1) {
      try {
        return JSON.parse(source);
      } catch (error) {
        const decoded = source.replace(/&(?:quot|apos|amp|lt|gt|#\d+|#x[\da-f]+);/gi, (entity) => {
          const named = { '&quot;': '"', '&apos;': "'", '&amp;': '&', '&lt;': '<', '&gt;': '>' };
          const key = entity.toLowerCase();
          if (Object.prototype.hasOwnProperty.call(named, key)) return named[key];
          const code = key.startsWith('&#x') ? parseInt(key.slice(3, -1), 16) : Number(key.slice(2, -1));
          return code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : entity;
        });
        if (decoded === source) break;
        source = decoded;
      }
    }
    return null;
  };

  const parseProperties = (input) => {
    const parsed = parseJson(input);
    if (isObject(parsed)) return parsed;
    if (!Array.isArray(parsed)) return {};
    return Object.fromEntries(parsed.filter((entry) => Array.isArray(entry) && typeof entry[0] === 'string'));
  };

  const collectAssets = (properties, expression, family) => Object.entries(properties)
    .flatMap(([key, value]) => {
      const match = key.match(expression);
      if (!match || !text(value)) return [];
      return [{
        value: text(value), family, index: Number(match[1] || 0),
        zoneId: text(properties[`${key}_zone_id`]), viewId: text(properties[`${key}_view_id`]),
        label: text(match[2]),
      }];
    }).sort((left, right) => left.index - right.index);

  const summarize = (input, options = {}) => {
    const properties = parseProperties(input);
    const labels = isObject(options.labels) ? options.labels : {};
    const branding = collectAssets(properties, /^_tm_branding_design_(\d+)$/u, 'branding');
    const studio = collectAssets(properties, /^_tm_studio_artwork_(\d+)$/u, 'studio');
    const legacyBranding = branding.length ? [] : collectAssets(
      properties, /^(?:Design original|Original design)(?:\s+(\d+))?(?:\s*-\s*(.*))?$/iu, 'branding',
    ).concat(collectAssets(properties, /^Logo\s*-(\s*)(.*)$/iu, 'branding'));
    const legacyStudio = studio.length ? [] : collectAssets(properties, /^Studio artwork\s+(\d+)$/iu, 'studio');
    const assets = [...branding, ...legacyBranding, ...studio, ...legacyStudio];
    if (!assets.length) return '';

    const manifest = parseJson(properties._tm_studio_preview_manifest) || parseJson(properties._studio_cart_preview_manifest);
    const views = Array.isArray(manifest?.views) ? manifest.views.filter(isObject) : [];
    const brandingZones = parseJson(properties._tm_branding_logo_zone_ids) || parseJson(properties._tm_branding_zone_ids);
    const legacyZones = text(properties._marking_logo_zone_keys || properties._marking_zone_keys).split(',').filter(Boolean);
    const zoneIds = Array.isArray(brandingZones) ? brandingZones.filter((zone) => text(zone)) : legacyZones;
    const studioZone = text(properties._tm_studio_zone_id || properties._studio_zone_key);
    const studioView = text(properties._tm_studio_view_id || properties._studio_view);
    const studioLabel = text(properties['Studio - Zone'] || properties['Studio - Vue'] || properties['Studio - View'] || properties['Studio - Placement']);

    const localizedLabel = (value) => {
      const key = normalizeZone(value);
      if (!key) return '';
      const translation = text(labels[key]);
      return translation ? translation.toLocaleLowerCase() : '';
    };
    const resolvePlacement = (asset) => {
      const zoneId = asset.zoneId || (asset.family === 'studio' ? studioZone : (zoneIds.length === 1 ? text(zoneIds[0]) : ''));
      const viewId = asset.viewId || (asset.family === 'studio' ? studioView : '');
      const matches = (reference, candidates) => reference && candidates.some((candidate) => normalizeZone(candidate) === normalizeZone(reference));
      const zoneView = zoneId && views.find((view) => matches(zoneId, [view.zoneId, view.placementId, view.viewId, view.key]));
      const matchingViews = viewId ? views.filter((view) => matches(viewId, [view.viewId, view.key, view.sourceViewId])) : [];
      const view = zoneView || (matchingViews.length === 1 ? matchingViews[0] : null);
      const rawLabel = text(asset.label || view?.zoneLabel || view?.label || (asset.family === 'studio' ? studioLabel : ''));
      const label = localizedLabel(zoneId) || localizedLabel(rawLabel) || localizedLabel(viewId) || rawLabel.toLocaleLowerCase();
      // Retain distinct opaque placement IDs, even when their displayed names match.
      const placement = normalizeZone(zoneId || view?.zoneId || rawLabel || viewId);
      return { ...asset, placement, label };
    };

    const resolved = assets.map(resolvePlacement);
    // Older Studio mirrors can omit placement metadata; infer it only when that
    // exact original has one unambiguous placement elsewhere on this cart line.
    resolved.forEach((asset) => {
      if (asset.placement) return;
      const matches = resolved.filter((candidate) => candidate.value === asset.value && candidate.placement);
      if (matches.length && new Set(matches.map((candidate) => candidate.placement)).size === 1) {
        asset.placement = matches[0].placement;
        asset.label = matches[0].label;
      }
    });
    const seen = new Set();
    const groups = new Map();
    resolved.forEach((asset) => {
      const identity = JSON.stringify([asset.value, asset.placement]);
      if (seen.has(identity)) return;
      seen.add(identity);
      groups.set(asset.label, (groups.get(asset.label) || 0) + 1);
    });
    return Array.from(groups, ([label, count]) => {
      const template = text(count === 1 ? options.designOne : options.designOther) || (count === 1 ? '__COUNT__ design' : '__COUNT__ designs');
      const quantity = template.replace(/__COUNT__|\{\{\s*count\s*\}\}/g, String(count));
      return label ? `${quantity} ${label}` : quantity;
    }).join(' ; ');
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = { summarize, parseProperties };
  if (typeof customElements === 'undefined' || typeof HTMLElement === 'undefined' || customElements.get('quote-cart-personalization')) return;

  customElements.define('quote-cart-personalization', class extends HTMLElement {
    connectedCallback() {
      if (!this.renderSummary() && typeof requestAnimationFrame === 'function') {
        // A registered element can connect before its parser-created children.
        requestAnimationFrame(() => { if (this.isConnected) this.renderSummary(); });
      }
    }

    renderSummary() {
      const item = this.closest('.quote-cart-item');
      const value = this.querySelector('[data-personalization-value]');
      if (!item || !value) return false;
      const labels = { ...(parseJson(item.querySelector('[data-studio-cart-labels]')?.textContent) || {}) };
      if (this.dataset.neckLabel) labels.neck = this.dataset.neckLabel;
      if (this.dataset.labelLabel) labels.label = this.dataset.labelLabel;
      const summary = summarize(item.querySelector('[data-cart-line-properties]')?.textContent, {
        labels,
        designOne: this.dataset.designOne,
        designOther: this.dataset.designOther,
      });
      value.textContent = summary;
      (this.querySelector('[data-personalization-row]') || this).hidden = !summary;
      return true;
    }
  });
})();
